import React, { Fragment, useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { orderMap } from '@constants';
import { ACCOUNT_RESOURCE_STATUS } from '@enums';
import { useStore } from '@hooks';
import { Loader, Table } from '@ui';
import { camelToSnakeCase, filterNonDemoProducts } from '@utils';
import { Empty } from 'antd';
import { observer } from 'mobx-react';
import EmptyState from './components/EmptyState';
import Header from './components/Header';
import NoProductsFound from './components/NoProductsFound';
import NotConnected from './components/NotConnected';
import ProductsSyncProgress from './components/ProductsSyncProgress';
import ProductVariantsTable from './components/ProductVariantsTable';
import SyncBanner from './components/SyncBanner';
import SyncFailed from './components/SyncFailed';
import { useProgress } from './hooks/useProgress';
import { getColumns, METHODS } from './constants';

const Products = observer(() => {
    const store = useStore();
    const [searchParams, setSearchParams] = useSearchParams();
    const { products, getProducts, pending, meta, updateFilters, updateSorters, resetFilters, resetSorters } =
        store.productsStore;
    const {
        isUninstalled,
        productsSyncProgress,
        currentAccount: { productTypes, platformId },
        getAccountProductTypes,
        isManual,
        isUpdateCsv
    } = store.accountsStore;
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [isPolling, setIsPolling] = useState(false);
    const pollingTimeoutRef = useRef(null);
    const attemptCountRef = useRef(0);
    const inProgressRef = useRef(false);
    const isCancelledRef = useRef(false);
    const maxAttempts = 10;
    const pollInterval = 5000;

    const showProgress = useProgress(setLoading);

    const filteredProducts = filterNonDemoProducts(products);

    useEffect(() => {
        setLoading(true);
        (async () => {
            if (isUninstalled) {
                setLoading(false);
            } else {
                const promises = [getProducts(), !productTypes.length ? getAccountProductTypes() : null].filter(
                    Boolean
                );

                await Promise.all(promises);

                setLoading(false);
            }
        })();

        return () => {
            resetFilters();
            resetSorters();
        };
    }, [productsSyncProgress?.status]);

    const loadProducts = searchParams.get('loadProducts') === 'true';

    useEffect(() => {
        if (!loadProducts) {
            setIsPolling(false);
            return;
        }

        attemptCountRef.current = 0;
        inProgressRef.current = false;
        isCancelledRef.current = false;
        setIsPolling(true);

        const checkProducts = async () => {
            if (isCancelledRef.current || inProgressRef.current) {
                return;
            }

            inProgressRef.current = true;
            attemptCountRef.current += 1;

            try {
                await getProducts();

                if (isCancelledRef.current) {
                    return;
                }

                const filteredProducts = filterNonDemoProducts(store.productsStore.products || []);
                const hasProducts = (filteredProducts?.length || 0) > 0;

                if (hasProducts || attemptCountRef.current >= maxAttempts) {
                    setIsPolling(false);
                    searchParams.delete('loadProducts');
                    setSearchParams(searchParams, { replace: true });
                    return;
                }

                if (!isCancelledRef.current) {
                    pollingTimeoutRef.current = setTimeout(() => {
                        inProgressRef.current = false;
                        checkProducts();
                    }, pollInterval);
                }
            } catch (error) {
                if (!isCancelledRef.current) {
                    setIsPolling(false);
                    searchParams.delete('loadProducts');
                    setSearchParams(searchParams, { replace: true });
                }
            } finally {
                inProgressRef.current = false;
            }
        };

        checkProducts();

        return () => {
            isCancelledRef.current = true;
            if (pollingTimeoutRef.current) {
                clearTimeout(pollingTimeoutRef.current);
                pollingTimeoutRef.current = null;
            }
            inProgressRef.current = false;
            setIsPolling(false);
        };
    }, [loadProducts, getProducts, store.productsStore, setSearchParams]);

    const handleTableChange = (pagination, filters, sorter) => {
        const page = pagination?.current;

        updateSorters({
            sort: sorter.order ? camelToSnakeCase(sorter.field) : undefined,
            order: sorter.order ? orderMap[sorter.order] : undefined
        });

        updateFilters({
            typeIds: filters.type || undefined,
            klickly_statuses: filters.klicklyStatus || undefined,
            ecommerce_statuses: filters.ecommerceStatus || undefined
        });

        getProducts({ page });
    };

    const handleSearch = (search) => {
        const escapedSearchValue = encodeURIComponent(`${search}`);
        updateFilters({
            search: search ? `title:${escapedSearchValue}` : undefined
        });
        getProducts();
        setSearch(search); // to avoid flickering of the table when search is cleared
    };

    if (loading || isPolling) {
        return (
            <Loader style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 9999 }} size='120px' />
        );
    }

    if (isUninstalled) {
        return <NotConnected />;
    }

    const syncFailed = productsSyncProgress?.status === ACCOUNT_RESOURCE_STATUS.FAILED;
    const hasNonDemoProducts = filteredProducts.length > 0;

    if ((isManual && syncFailed && !hasNonDemoProducts) || (!isManual && syncFailed)) {
        return <SyncFailed />;
    }

    if (showProgress) {
        return <ProductsSyncProgress />;
    }

    if (!hasNonDemoProducts && !search && !pending) {
        return <EmptyState />;
    }

    return (
        <Fragment>
            <Header showSearch setSearchValue={handleSearch} showAddProductsButton={isManual && !isUpdateCsv} />
            {isUpdateCsv && <SyncBanner method={METHODS.ONLY_CSV} />}
            {isManual && syncFailed && hasNonDemoProducts && <SyncBanner method={METHODS.FAILED_BUT_HAS_PRODUCTS} />}
            {search && !hasNonDemoProducts && !pending ? <NoProductsFound /> : null}
            {hasNonDemoProducts || pending ? (
                <Table
                    data-e2e='products_table'
                    dataSource={filteredProducts}
                    columns={getColumns(productTypes, platformId)}
                    rowKey={(record) => record.id}
                    loading={pending}
                    pagination={{
                        pageSize: meta?.perPage,
                        total: meta?.total,
                        totalPages: meta?.totalPages,
                        current: meta?.page,
                        showSizeChanger: false
                    }}
                    onChange={handleTableChange}
                    expandable={{
                        expandedRowRender: (record) => <ProductVariantsTable productId={record.id} />
                    }}
                    expandPosition='left'
                    scroll={{
                        x: 1250
                    }}
                    getRowProps={(record) => ({
                        'data-e2e': `product_${record.id}_row`
                    })}
                    locale={{
                        emptyText: (
                            <Empty
                                image={Empty.PRESENTED_IMAGE_SIMPLE}
                                description={<div data-e2e='no-data_placeholder'>No data</div>}
                            />
                        )
                    }}
                />
            ) : null}
        </Fragment>
    );
});

export default Products;
